// Popover — the floating step content. Branded variant: gradient white→blue
// background with a teal primary border. Self-positions next to the anchor
// rect using the requested placement and clamps to the viewport.
//
// When `rect` is null the popover renders centered in the viewport (used for
// step 1 (the banner) and as a graceful fallback if an anchor was queried
// before its DOM node existed).

import { useLayoutEffect, useRef, useState } from 'react';
import type { TourRect, TourStepPlacement } from './types';

const POPOVER_WIDTH = 340;
const MARGIN = 16;
const VIEWPORT_PAD = 12;

function computePosition(
  rect: TourRect | null,
  placement: TourStepPlacement,
  popW: number,
  popH: number,
): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect) {
    return {
      left: Math.max(VIEWPORT_PAD, vw / 2 - popW / 2),
      top: Math.max(VIEWPORT_PAD, vh / 2 - popH / 2),
    };
  }

  let left: number;
  let top: number;

  switch (placement) {
    case 'right':
      left = rect.right + MARGIN;
      top = rect.top + rect.height / 2 - popH / 2;
      break;
    case 'left':
      left = rect.left - popW - MARGIN;
      top = rect.top + rect.height / 2 - popH / 2;
      break;
    case 'top':
      left = rect.left + rect.width / 2 - popW / 2;
      top = rect.top - popH - MARGIN;
      break;
    case 'bottom':
    default:
      left = rect.left + rect.width / 2 - popW / 2;
      top = rect.bottom + MARGIN;
      break;
  }

  // Clamp into viewport so the popover never disappears off-screen.
  left = Math.max(VIEWPORT_PAD, Math.min(left, vw - popW - VIEWPORT_PAD));
  top = Math.max(VIEWPORT_PAD, Math.min(top, vh - popH - VIEWPORT_PAD));
  return { left, top };
}

export type PopoverProps = {
  rect: TourRect | null;
  title: string;
  text: string;
  placement: TourStepPlacement;
  index: number;
  total: number;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
};

export default function Popover({
  rect,
  title,
  text,
  placement,
  index,
  total,
  onBack,
  onNext,
  onSkip,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Recompute position whenever the rect moves OR the popover's own height
  // changes (longer step body → taller popover → different vertical offset
  // for left/right placements).
  useLayoutEffect(() => {
    if (!ref.current) return;
    const w = ref.current.offsetWidth || POPOVER_WIDTH;
    const h = ref.current.offsetHeight;
    setPos(computePosition(rect, placement, w, h));
  }, [rect, placement, title, text]);

  const isLast = index === total - 1;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby="atad-tour-step-title"
      className="fixed z-50 rounded-half p-5 transition-all duration-200 bg-gradient-to-br from-white to-blue-50 border-2 border-primary shadow-2xl"
      style={{
        width: POPOVER_WIDTH,
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        opacity: pos ? 1 : 0,
        backgroundColor: 'teal',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold tracking-widest uppercase text-primary-dark">
          Step {index + 1} of {total}
        </span>
        <button
          type="button"
          onClick={onSkip}
          className="text-xs font-bold text-blue hover:text-primary"
        >
          Skip tour
        </button>
      </div>

      <h3
        id="atad-tour-step-title"
        className="text-base font-bold mb-2 text-gray-900"
      >
        {title}
      </h3>
      <p className="text-sm leading-relaxed mb-4 text-gray-600">{text}</p>

      {/* Progress dots */}
      <div className="flex gap-1.5 mb-4" aria-hidden="true">
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={[
              'h-1.5 rounded-full transition-all',
              i === index
                ? 'w-6 bg-primary'
                : i < index
                  ? 'w-1.5 bg-primary-light'
                  : 'w-1.5 bg-gray-300',
            ].join(' ')}
          />
        ))}
      </div>

      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={onBack}
          disabled={index === 0}
          className={[
            'text-sm font-bold px-3 py-1 rounded-full',
            index === 0
              ? 'text-gray-300 cursor-default'
              : 'text-blue hover:text-primary',
          ].join(' ')}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="bg-primary border-3 border-primary-dark hover:border-primary text-white font-extrabold rounded-full px-5 py-1 text-sm"
        >
          {isLast ? 'Done' : 'Next →'}
        </button>
      </div>
    </div>
  );
}
